import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Gauge, Code2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DeferredContactModal } from "../DeferredContactModal";
import { useTranslation } from "react-i18next";
// Import direct (pas de lazy) : le chunk n'attend plus le réseau,
// l'animation démarre dès la première frame au lieu d'arriver en retard.
import { InkFlowField } from "../InkFlowField";

export function HomeHero() {
  const { t } = useTranslation();

  return (
    <section className="relative pt-16 pb-20 md:pt-24 md:pb-32 overflow-hidden min-h-[85vh] flex items-center">
      {/* Fond chaud statique derrière le fluide (peint une fois, coût nul) */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(55% 45% at 50% 38%, rgba(191,38,11,0.14), transparent 70%)",
          contain: "paint",
        }}
      />
      {/* TON ANIMATION — démarrage immédiat, mêmes réglages */}
      <InkFlowField
        colors={["#BF260B", "#FF4500", "#FFAA00", "#FF0055", "#9333EA"]}
        speed={80}
        dissipation={42}
        swirl={28}
        drift={18}
        glow="rgba(191, 38, 11, 0.15)"
        cursor={{ force: 65, reach: 45 }}
      />

      {/* Grille statique ultra-légère */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.03]">
        <div className="absolute inset-0 hero-grid" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10 pointer-events-auto">
        <div className="max-w-4xl mx-auto text-center">
          {/* Studio Badge — sans backdrop-blur ni animate-pulse */}
          <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-border/80 bg-card mb-6 sm:mb-8 shadow-xs">
            <span className="flex h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold tracking-wide text-foreground/90 uppercase flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-primary-ink" /> {t('home_modern.hero.badge')}
            </span>
          </div>

          {/* Main Headline (Instant LCP & 100% SEO Crawler Discovery) */}
          <h1 className="text-2xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-6 sm:mb-8 leading-[1.15] sm:leading-[1.08] text-foreground font-heading break-words">
            {t('home_modern.hero.h1_prefix')}{" "}
            <span className="bg-gradient-to-r from-primary via-orange-500 to-amber-500 bg-clip-text text-transparent">
              {t('home_modern.hero.h1_highlight')}
            </span>{" "}
            {t('home_modern.hero.h1_suffix')}
          </h1>

          {/* Subtitle - Instant LCP Element */}
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed font-normal">
            {t('home_modern.hero.desc')}
          </p>

          {/* Call to Actions — boutons simples, aucun tracker mousemove */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16 w-full max-w-md mx-auto sm:max-w-none">
            <DeferredContactModal
              defaultPack="Croissance"
              defaultMessage={t('contact_extra.default_project_msg', "Bonjour, j'aimerais échanger sur la création / refonte de mon site web.")}
            >
              <Button size="lg" className="rounded-full px-8 h-12 sm:h-14 text-sm sm:text-base font-semibold shadow-lg shadow-primary/20 w-full sm:w-auto">
                {t('home_modern.hero.cta_launch')} <ArrowRight className="ms-2 w-4 h-4 rtl:rotate-180" />
              </Button>
            </DeferredContactModal>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-8 h-12 sm:h-14 text-sm sm:text-base font-medium border-border/80 hover:bg-muted/50 w-full sm:w-auto"
              render={<Link to="/audit" />}
              nativeButton={false}
            >
              {t('home_modern.hero.cta_audit')}
            </Button>
          </div>

          {/* Direct Proof Metrics */}
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 pt-6 sm:pt-8 border-t border-border/60 max-w-3xl mx-auto text-start">
            <div className="flex items-center gap-3 p-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <Gauge className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">{t('home_modern.hero.metric_speed_title')}</div>
                <div className="text-xs text-muted-foreground">{t('home_modern.hero.metric_speed_sub')}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <Code2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">{t('home_modern.hero.metric_prop_title')}</div>
                <div className="text-xs text-muted-foreground">{t('home_modern.hero.metric_prop_sub')}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">{t('home_modern.hero.metric_sec_title')}</div>
                <div className="text-xs text-muted-foreground">{t('home_modern.hero.metric_sec_sub')}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">{t('home_modern.hero.metric_local_title')}</div>
                <div className="text-xs text-muted-foreground">{t('home_modern.hero.metric_local_sub')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
